// components/Loader.jsx
import React from "react";

const Loader = ({ variant = "rings" }) => {
  // ================= 1. TRIPLE RINGS =================
  if (variant === "rings") {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="advanced-loader">
          <div className="loader-ring"></div>
          <div className="loader-ring"></div>
          <div className="loader-ring"></div>
          <div className="loader-core"></div>
          <p className="loader-text">Loading</p>
        </div>
        <style jsx>{`
          .advanced-loader { position: relative; width: 120px; height: 120px; display: flex; justify-content: center; align-items: center; }
          .loader-ring { position: absolute; border-radius: 50%; border: 3px solid transparent; animation: rotate 1.5s linear infinite; }
          .loader-ring:nth-child(1) { width: 120px; height: 120px; border-top-color: #6366f1; border-right-color: #6366f1; animation-duration: 1.2s; }
          .loader-ring:nth-child(2) { width: 90px; height: 90px; border-bottom-color: #8b5cf6; border-left-color: #8b5cf6; animation-duration: 1s; animation-direction: reverse; }
          .loader-ring:nth-child(3) { width: 60px; height: 60px; border-top-color: #ec4899; border-right-color: #ec4899; animation-duration: 0.8s; }
          .loader-core { position: absolute; width: 12px; height: 12px; background: linear-gradient(135deg, #6366f1, #ec4899); border-radius: 50%; animation: pulse 1.2s ease-in-out infinite; box-shadow: 0 0 20px rgba(99,102,241,0.8), 0 0 40px rgba(236,72,153,0.5); }
          .loader-text { position: absolute; bottom: -50px; margin: 0; font-size: 16px; font-weight: 600; letter-spacing: 3px; text-transform: uppercase; background: linear-gradient(90deg, #6366f1, #8b5cf6, #ec4899, #6366f1); background-size: 200% auto; -webkit-background-clip: text; -webkit-text-fill-color: transparent; animation: shimmer 2s linear infinite; }
          @keyframes rotate { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          @keyframes pulse { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.5); opacity: 0.7; } }
          @keyframes shimmer { to { background-position: 200% center; } }
        `}</style>
      </div>
    );
  }

  // ================= 2. NEON DOTS =================
  if (variant === "dots") {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center vh-100">
        <div className="dot-loader"><span></span><span></span><span></span><span></span><span></span></div>
        <p className="dot-text">Loading</p>
        <style jsx>{`
          .dot-loader { display: flex; align-items: center; gap: 12px; }
          .dot-loader span { width: 18px; height: 18px; border-radius: 50%; display: inline-block; animation: wave 1.2s ease-in-out infinite; }
          .dot-loader span:nth-child(1) { background: #6366f1; box-shadow: 0 0 15px #6366f1; }
          .dot-loader span:nth-child(2) { background: #8b5cf6; animation-delay: 0.15s; box-shadow: 0 0 15px #8b5cf6; }
          .dot-loader span:nth-child(3) { background: #a855f7; animation-delay: 0.3s; box-shadow: 0 0 15px #a855f7; }
          .dot-loader span:nth-child(4) { background: #d946ef; animation-delay: 0.45s; box-shadow: 0 0 15px #d946ef; }
          .dot-loader span:nth-child(5) { background: #ec4899; animation-delay: 0.6s; box-shadow: 0 0 15px #ec4899; }
          .dot-text { margin-top: 30px; font-size: 14px; font-weight: 600; letter-spacing: 4px; text-transform: uppercase; color: #8b5cf6; animation: fade 1.5s ease-in-out infinite; }
          @keyframes wave { 0%,100% { transform: translateY(0) scale(1); opacity: 1; } 50% { transform: translateY(-20px) scale(1.3); opacity: 0.6; } }
          @keyframes fade { 0%,100% { opacity: 0.4; } 50% { opacity: 1; } }
        `}</style>
      </div>
    );
  }

  // ================= 3. PULSING BARS =================
  if (variant === "bars") {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center vh-100">
        <div className="bar-loader"><span></span><span></span><span></span><span></span><span></span></div>
        <p className="bar-text">Loading</p>
        <style jsx>{`
          .bar-loader { display: flex; align-items: flex-end; gap: 8px; height: 70px; }
          .bar-loader span { width: 10px; border-radius: 6px; animation: barGrow 1s ease-in-out infinite; }
          .bar-loader span:nth-child(1) { background: linear-gradient(180deg, #6366f1, #8b5cf6); }
          .bar-loader span:nth-child(2) { background: linear-gradient(180deg, #8b5cf6, #a855f7); animation-delay: 0.1s; }
          .bar-loader span:nth-child(3) { background: linear-gradient(180deg, #a855f7, #d946ef); animation-delay: 0.2s; }
          .bar-loader span:nth-child(4) { background: linear-gradient(180deg, #d946ef, #ec4899); animation-delay: 0.3s; }
          .bar-loader span:nth-child(5) { background: linear-gradient(180deg, #ec4899, #f43f5e); animation-delay: 0.4s; }
          .bar-text { margin-top: 30px; font-size: 14px; font-weight: 600; letter-spacing: 4px; text-transform: uppercase; background: linear-gradient(90deg, #6366f1, #ec4899, #6366f1); background-size: 200% auto; -webkit-background-clip: text; -webkit-text-fill-color: transparent; animation: shimmer 2s linear infinite; }
          @keyframes barGrow { 0%,100% { height: 15px; opacity: 0.5; } 50% { height: 70px; opacity: 1; } }
          @keyframes shimmer { to { background-position: 200% center; } }
        `}</style>
      </div>
    );
  }

  // ================= 4. ORBITING SPINNER =================
  if (variant === "orbit") {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center vh-100">
        <div className="orbit-loader">
          <div className="orbit-center"></div>
          <div className="orbit-path"><div className="orbit-dot"></div></div>
          <div className="orbit-path orbit-path-2"><div className="orbit-dot orbit-dot-2"></div></div>
          <div className="orbit-path orbit-path-3"><div className="orbit-dot orbit-dot-3"></div></div>
        </div>
        <p className="orbit-text">Loading</p>
        <style jsx>{`
          .orbit-loader { position: relative; width: 120px; height: 120px; display: flex; justify-content: center; align-items: center; }
          .orbit-center { width: 24px; height: 24px; border-radius: 50%; background: radial-gradient(circle, #ec4899, #6366f1); box-shadow: 0 0 25px rgba(236,72,153,0.9), 0 0 50px rgba(99,102,241,0.6); animation: pulse 1.5s ease-in-out infinite; }
          .orbit-path { position: absolute; width: 60px; height: 60px; border-radius: 50%; border: 1px dashed rgba(139,92,246,0.3); animation: rotate 2s linear infinite; }
          .orbit-path-2 { width: 90px; height: 90px; animation: rotate 3s linear infinite reverse; }
          .orbit-path-3 { width: 120px; height: 120px; animation: rotate 4s linear infinite; }
          .orbit-dot { position: absolute; top: -5px; left: 50%; transform: translateX(-50%); width: 10px; height: 10px; border-radius: 50%; background: #6366f1; box-shadow: 0 0 15px #6366f1; }
          .orbit-dot-2 { background: #a855f7; box-shadow: 0 0 15px #a855f7; }
          .orbit-dot-3 { background: #ec4899; box-shadow: 0 0 15px #ec4899; }
          .orbit-text { margin-top: 30px; font-size: 14px; font-weight: 600; letter-spacing: 4px; text-transform: uppercase; color: #a855f7; animation: fade 1.5s ease-in-out infinite; }
          @keyframes rotate { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          @keyframes pulse { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.4); opacity: 0.7; } }
          @keyframes fade { 0%,100% { opacity: 0.4; } 50% { opacity: 1; } }
        `}</style>
      </div>
    );
  }

  // ================= 5. DNA HELIX (Lab themed) =================
  if (variant === "dna") {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center vh-100">
        <div className="dna-loader">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="dna-pair" style={{ animationDelay: `${i * 0.15}s` }}>
              <span className="dna-dot dna-dot-left"></span>
              <span className="dna-bar"></span>
              <span className="dna-dot dna-dot-right"></span>
            </div>
          ))}
        </div>
        <p className="dna-text">Analyzing Samples</p>
        <style jsx>{`
          .dna-loader { display: flex; flex-direction: column; gap: 8px; height: 180px; justify-content: center; }
          .dna-pair { display: flex; align-items: center; gap: 6px; animation: dnaSpin 2s ease-in-out infinite; transform-origin: center; }
          .dna-dot { width: 10px; height: 10px; border-radius: 50%; }
          .dna-dot-left { background: #06b6d4; box-shadow: 0 0 12px #06b6d4; }
          .dna-dot-right { background: #ec4899; box-shadow: 0 0 12px #ec4899; }
          .dna-bar { width: 30px; height: 2px; background: linear-gradient(90deg, #06b6d4, #ec4899); border-radius: 2px; opacity: 0.6; }
          .dna-text { margin-top: 30px; font-size: 13px; font-weight: 600; letter-spacing: 4px; text-transform: uppercase; color: #06b6d4; animation: fade 1.5s ease-in-out infinite; }
          @keyframes dnaSpin { 0%,100% { transform: scaleX(1); } 50% { transform: scaleX(0.2); } }
          @keyframes fade { 0%,100% { opacity: 0.4; } 50% { opacity: 1; } }
        `}</style>
      </div>
    );
  }

  // ================= 6. PETRI DISH / CELL PULSE (Lab themed) =================
  if (variant === "petri") {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center vh-100">
        <div className="petri-loader">
          <div className="petri-dish">
            <div className="petri-cell petri-cell-1"></div>
            <div className="petri-cell petri-cell-2"></div>
            <div className="petri-cell petri-cell-3"></div>
            <div className="petri-cell petri-cell-4"></div>
            <div className="petri-cell petri-cell-5"></div>
          </div>
        </div>
        <p className="petri-text">Incubating</p>
        <style jsx>{`
          .petri-loader { display: flex; justify-content: center; align-items: center; }
          .petri-dish { position: relative; width: 120px; height: 120px; border-radius: 50%; border: 2px solid rgba(6,182,212,0.5); background: radial-gradient(circle, rgba(6,182,212,0.08), transparent 70%); box-shadow: 0 0 25px rgba(6,182,212,0.3), inset 0 0 25px rgba(6,182,212,0.2); display: flex; justify-content: center; align-items: center; }
          .petri-cell { position: absolute; border-radius: 50%; animation: cellPulse 1.8s ease-in-out infinite; }
          .petri-cell-1 { width: 20px; height: 20px; background: #06b6d4; top: 20px; left: 20px; box-shadow: 0 0 15px #06b6d4; animation-delay: 0s; }
          .petri-cell-2 { width: 14px; height: 14px; background: #10b981; top: 30px; right: 25px; box-shadow: 0 0 15px #10b981; animation-delay: 0.3s; }
          .petri-cell-3 { width: 24px; height: 24px; background: #ec4899; bottom: 25px; left: 30px; box-shadow: 0 0 15px #ec4899; animation-delay: 0.6s; }
          .petri-cell-4 { width: 16px; height: 16px; background: #8b5cf6; bottom: 30px; right: 25px; box-shadow: 0 0 15px #8b5cf6; animation-delay: 0.9s; }
          .petri-cell-5 { width: 18px; height: 18px; background: #f59e0b; top: 50%; left: 50%; transform: translate(-50%, -50%); box-shadow: 0 0 15px #f59e0b; animation-delay: 1.2s; }
          .petri-text { margin-top: 30px; font-size: 13px; font-weight: 600; letter-spacing: 4px; text-transform: uppercase; color: #06b6d4; animation: fade 1.5s ease-in-out infinite; }
          @keyframes cellPulse { 0%,100% { transform: scale(1); opacity: 0.7; } 50% { transform: scale(1.4); opacity: 1; } }
          @keyframes fade { 0%,100% { opacity: 0.4; } 50% { opacity: 1; } }
        `}</style>
      </div>
    );
  }

  // ================= 7. VIRUS SCAN (Polio themed) =================
  if (variant === "virus") {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center vh-100">
        <div className="virus-loader">
          <div className="virus-ring virus-ring-1"></div>
          <div className="virus-ring virus-ring-2"></div>
          <div className="virus-ring virus-ring-3"></div>
          <div className="virus-core">
            <div className="virus-spike virus-spike-1"></div>
            <div className="virus-spike virus-spike-2"></div>
            <div className="virus-spike virus-spike-3"></div>
            <div className="virus-spike virus-spike-4"></div>
            <div className="virus-spike virus-spike-5"></div>
            <div className="virus-spike virus-spike-6"></div>
          </div>
        </div>
        <p className="virus-text">Scanning Virus</p>
        <style jsx>{`
          .virus-loader { position: relative; width: 130px; height: 130px; display: flex; justify-content: center; align-items: center; }
          .virus-ring { position: absolute; border-radius: 50%; border: 2px solid transparent; animation: rotate 2s linear infinite; }
          .virus-ring-1 { width: 130px; height: 130px; border-top-color: #ef4444; border-left-color: #ef4444; animation-duration: 2s; }
          .virus-ring-2 { width: 100px; height: 100px; border-bottom-color: #f97316; border-right-color: #f97316; animation-duration: 1.5s; animation-direction: reverse; }
          .virus-ring-3 { width: 70px; height: 70px; border-top-color: #facc15; border-left-color: #facc15; animation-duration: 1s; }
          .virus-core { position: relative; width: 40px; height: 40px; border-radius: 50%; background: radial-gradient(circle, #ef4444, #b91c1c); box-shadow: 0 0 30px #ef4444, 0 0 60px rgba(239,68,68,0.5); animation: virusPulse 1.5s ease-in-out infinite; display: flex; justify-content: center; align-items: center; }
          .virus-spike { position: absolute; width: 4px; height: 10px; background: #facc15; border-radius: 2px; box-shadow: 0 0 8px #facc15; transform-origin: center 30px; }
          .virus-spike-1 { transform: rotate(0deg) translateY(-30px); }
          .virus-spike-2 { transform: rotate(60deg) translateY(-30px); }
          .virus-spike-3 { transform: rotate(120deg) translateY(-30px); }
          .virus-spike-4 { transform: rotate(180deg) translateY(-30px); }
          .virus-spike-5 { transform: rotate(240deg) translateY(-30px); }
          .virus-spike-6 { transform: rotate(300deg) translateY(-30px); }
          .virus-text { margin-top: 30px; font-size: 13px; font-weight: 700; letter-spacing: 4px; text-transform: uppercase; color: #ef4444; animation: fade 1.5s ease-in-out infinite; }
          @keyframes rotate { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          @keyframes virusPulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.15); } }
          @keyframes fade { 0%,100% { opacity: 0.5; } 50% { opacity: 1; } }
        `}</style>
      </div>
    );
  }

  // ================= 8. TEST TUBE / VIAL FILLING (Lab themed) =================
  if (variant === "vial") {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center vh-100">
        <div className="vial-loader">
          <div className="vial">
            <div className="vial-liquid"></div>
            <div className="vial-bubble vial-bubble-1"></div>
            <div className="vial-bubble vial-bubble-2"></div>
            <div className="vial-bubble vial-bubble-3"></div>
          </div>
          <div className="vial vial-2">
            <div className="vial-liquid vial-liquid-2"></div>
            <div className="vial-bubble vial-bubble-1"></div>
            <div className="vial-bubble vial-bubble-2"></div>
          </div>
          <div className="vial vial-3">
            <div className="vial-liquid vial-liquid-3"></div>
            <div className="vial-bubble vial-bubble-1"></div>
            <div className="vial-bubble vial-bubble-3"></div>
          </div>
        </div>
        <p className="vial-text">Processing Samples</p>
        <style jsx>{`
          .vial-loader { display: flex; align-items: flex-end; gap: 16px; height: 120px; }
          .vial { position: relative; width: 22px; height: 100px; border: 2px solid #06b6d4; border-radius: 4px 4px 11px 11px; overflow: hidden; background: rgba(6,182,212,0.05); }
          .vial-2 { height: 85px; border-color: #10b981; }
          .vial-3 { height: 110px; border-color: #8b5cf6; }
          .vial-liquid { position: absolute; bottom: 0; left: 0; right: 0; height: 0%; background: linear-gradient(180deg, rgba(6,182,212,0.4), #06b6d4); animation: fillUp 2s ease-in-out infinite; }
          .vial-liquid-2 { background: linear-gradient(180deg, rgba(16,185,129,0.4), #10b981); animation-delay: 0.3s; }
          .vial-liquid-3 { background: linear-gradient(180deg, rgba(139,92,246,0.4), #8b5cf6); animation-delay: 0.6s; }
          .vial-bubble { position: absolute; width: 4px; height: 4px; border-radius: 50%; background: white; opacity: 0.7; animation: bubbleUp 1.5s ease-in infinite; }
          .vial-bubble-1 { left: 25%; bottom: 0; animation-delay: 0s; }
          .vial-bubble-2 { left: 55%; bottom: 0; animation-delay: 0.4s; }
          .vial-bubble-3 { left: 75%; bottom: 0; animation-delay: 0.8s; }
          .vial-text { margin-top: 30px; font-size: 13px; font-weight: 600; letter-spacing: 4px; text-transform: uppercase; color: #06b6d4; animation: fade 1.5s ease-in-out infinite; }
          @keyframes fillUp { 0%,100% { height: 20%; } 50% { height: 80%; } }
          @keyframes bubbleUp { 0% { transform: translateY(0); opacity: 0; } 20% { opacity: 0.9; } 100% { transform: translateY(-80px); opacity: 0; } }
          @keyframes fade { 0%,100% { opacity: 0.5; } 50% { opacity: 1; } }
        `}</style>
      </div>
    );
  }

  // ================= 9. HEARTBEAT / ECG PULSE (Medical) =================
  if (variant === "heartbeat") {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center vh-100">
        <div className="heartbeat-loader">
          <svg viewBox="0 0 300 100" className="heartbeat-svg">
            <polyline
              className="heartbeat-line"
              points="0,50 40,50 55,50 65,20 75,80 85,35 95,50 130,50 145,50 155,20 165,80 175,35 185,50 220,50 235,50 245,20 255,80 265,35 275,50 300,50"
            />
          </svg>
        </div>
        <p className="heartbeat-text">Loading Records</p>
        <style jsx>{`
          .heartbeat-loader { width: 300px; height: 100px; display: flex; justify-content: center; align-items: center; }
          .heartbeat-svg { width: 100%; height: 100%; }
          .heartbeat-line { fill: none; stroke: #10b981; stroke-width: 3; stroke-linecap: round; stroke-linejoin: round; stroke-dasharray: 600; stroke-dashoffset: 600; animation: draw 2s linear infinite; filter: drop-shadow(0 0 6px #10b981); }
          .heartbeat-text { margin-top: 20px; font-size: 13px; font-weight: 600; letter-spacing: 4px; text-transform: uppercase; color: #10b981; animation: fade 1.5s ease-in-out infinite; }
          @keyframes draw { 0% { stroke-dashoffset: 600; } 100% { stroke-dashoffset: 0; } }
          @keyframes fade { 0%,100% { opacity: 0.5; } 50% { opacity: 1; } }
        `}</style>
      </div>
    );
  }

  // ================= 10. MICROSCOPE LENS (Lab) =================
  if (variant === "microscope") {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center vh-100">
        <div className="micro-loader">
          <div className="micro-lens">
            <div className="micro-view">
              <div className="micro-organism micro-org-1"></div>
              <div className="micro-organism micro-org-2"></div>
              <div className="micro-organism micro-org-3"></div>
              <div className="micro-crosshair"></div>
            </div>
          </div>
          <div className="micro-scan"></div>
        </div>
        <p className="micro-text">Focusing</p>
        <style jsx>{`
          .micro-loader { position: relative; width: 140px; height: 140px; }
          .micro-lens { position: relative; width: 140px; height: 140px; border-radius: 50%; border: 4px solid #8b5cf6; box-shadow: 0 0 30px rgba(139,92,246,0.5), inset 0 0 20px rgba(139,92,246,0.3); overflow: hidden; background: radial-gradient(circle, rgba(139,92,246,0.15), transparent); }
          .micro-view { position: relative; width: 100%; height: 100%; }
          .micro-organism { position: absolute; border-radius: 50%; animation: microMove 3s ease-in-out infinite; }
          .micro-org-1 { width: 20px; height: 20px; background: #06b6d4; top: 30%; left: 30%; box-shadow: 0 0 12px #06b6d4; animation-delay: 0s; }
          .micro-org-2 { width: 14px; height: 14px; background: #10b981; top: 55%; left: 60%; box-shadow: 0 0 12px #10b981; animation-delay: 1s; }
          .micro-org-3 { width: 18px; height: 18px; background: #ec4899; top: 70%; left: 25%; box-shadow: 0 0 12px #ec4899; animation-delay: 2s; }
          .micro-crosshair { position: absolute; top: 50%; left: 50%; width: 30px; height: 30px; transform: translate(-50%,-50%); border: 1px solid rgba(255,255,255,0.4); border-radius: 50%; }
          .micro-crosshair::before, .micro-crosshair::after { content: ""; position: absolute; background: rgba(255,255,255,0.4); }
          .micro-crosshair::before { top: 50%; left: -10px; right: -10px; height: 1px; }
          .micro-crosshair::after { left: 50%; top: -10px; bottom: -10px; width: 1px; }
          .micro-scan { position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, transparent, #8b5cf6, transparent); box-shadow: 0 0 12px #8b5cf6; animation: scanDown 2s linear infinite; border-radius: 50%; }
          .micro-text { margin-top: 30px; font-size: 13px; font-weight: 600; letter-spacing: 4px; text-transform: uppercase; color: #8b5cf6; animation: fade 1.5s ease-in-out infinite; }
          @keyframes microMove { 0%,100% { transform: translate(0,0) scale(1); } 33% { transform: translate(15px,-10px) scale(1.2); } 66% { transform: translate(-10px,15px) scale(0.9); } }
          @keyframes scanDown { 0% { top: 0; } 100% { top: 100%; } }
          @keyframes fade { 0%,100% { opacity: 0.5; } 50% { opacity: 1; } }
        `}</style>
      </div>
    );
  }

  // ================= 11. VACCINE DROP (Polio themed) =================
  if (variant === "vaccine") {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center vh-100">
        <div className="vaccine-loader">
          <div className="vaccine-drop">
            <div className="drop-shape"></div>
          </div>
          <div className="vaccine-ripple vaccine-ripple-1"></div>
          <div className="vaccine-ripple vaccine-ripple-2"></div>
          <div className="vaccine-ripple vaccine-ripple-3"></div>
        </div>
        <p className="vaccine-text">Administering</p>
        <style jsx>{`
          .vaccine-loader { position: relative; width: 140px; height: 140px; display: flex; justify-content: center; align-items: center; }
          .vaccine-drop { position: relative; z-index: 2; animation: dropBounce 1.5s ease-in-out infinite; }
          .drop-shape { width: 30px; height: 30px; background: linear-gradient(180deg, #06b6d4, #0ea5e9); border-radius: 50% 50% 50% 0; transform: rotate(-45deg); box-shadow: 0 0 20px rgba(6,182,212,0.8); }
          .vaccine-ripple { position: absolute; border: 2px solid #06b6d4; border-radius: 50%; opacity: 0; animation: ripple 2s ease-out infinite; }
          .vaccine-ripple-1 { width: 40px; height: 40px; animation-delay: 0s; }
          .vaccine-ripple-2 { width: 40px; height: 40px; animation-delay: 0.6s; }
          .vaccine-ripple-3 { width: 40px; height: 40px; animation-delay: 1.2s; }
          .vaccine-text { margin-top: 30px; font-size: 13px; font-weight: 600; letter-spacing: 4px; text-transform: uppercase; color: #06b6d4; animation: fade 1.5s ease-in-out infinite; }
          @keyframes dropBounce { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-15px); } }
          @keyframes ripple { 0% { transform: scale(0.5); opacity: 0.8; } 100% { transform: scale(3.5); opacity: 0; } }
          @keyframes fade { 0%,100% { opacity: 0.5; } 50% { opacity: 1; } }
        `}</style>
      </div>
    );
  }

  // ================= 12. HEXAGON GRID (Tech/Advanced) =================
  if (variant === "hexagon") {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center vh-100">
        <div className="hex-loader">
          {[...Array(7)].map((_, i) => (
            <div key={i} className={`hex hex-${i + 1}`}></div>
          ))}
        </div>
        <p className="hex-text">Initializing</p>
        <style jsx>{`
          .hex-loader { position: relative; width: 120px; height: 120px; display: flex; justify-content: center; align-items: center; }
          .hex { position: absolute; width: 30px; height: 30px; background: transparent; border: 2px solid #6366f1; clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%); animation: hexPulse 2s ease-in-out infinite; box-shadow: 0 0 20px rgba(99,102,241,0.5); }
          .hex-1 { top: 0; left: 50%; transform: translateX(-50%); animation-delay: 0s; border-color: #6366f1; }
          .hex-2 { top: 30%; right: 0; animation-delay: 0.2s; border-color: #8b5cf6; }
          .hex-3 { bottom: 30%; right: 0; animation-delay: 0.4s; border-color: #a855f7; }
          .hex-4 { bottom: 0; left: 50%; transform: translateX(-50%); animation-delay: 0.6s; border-color: #d946ef; }
          .hex-5 { bottom: 30%; left: 0; animation-delay: 0.8s; border-color: #ec4899; }
          .hex-6 { top: 30%; left: 0; animation-delay: 1s; border-color: #f43f5e; }
          .hex-7 { top: 50%; left: 50%; transform: translate(-50%, -50%); animation-delay: 1.2s; border-color: #06b6d4; }
          .hex-text { margin-top: 30px; font-size: 13px; font-weight: 600; letter-spacing: 4px; text-transform: uppercase; color: #6366f1; animation: fade 1.5s ease-in-out infinite; }
          @keyframes hexPulse { 0%,100% { opacity: 0.3; transform: scale(0.85); } 50% { opacity: 1; transform: scale(1.15); } }
          @keyframes fade { 0%,100% { opacity: 0.5; } 50% { opacity: 1; } }
        `}</style>
      </div>
    );
  }

  return null;
};

export default Loader;